import { featureFlagSlugs } from './featureFlagSlugs';

/**
 * Static Feature Announcement content, shipped with the frontend (no backend API).
 * See docs/contexts/feature-announcements/CONTEXT.md and TRA-12121 Design/Decision docs.
 *
 * Adding a walkthrough-type announcement? Follow
 * docs/contexts/feature-announcements/authoring-a-walkthrough.md — it covers the step ids,
 * registering targets in components, and when a `setup` is needed.
 *
 * Authoring rules:
 * - `id` is the stable key for seen-tracking (localStorage) and the `announcement_id`
 *   in analytics. Never rename or reuse an `id` once it has shipped.
 * - `label` (a display category) and `type` (panel-click dispatch) are independent:
 *   e.g. `label: 'new-feature'` with `type: 'walkthrough'` is valid. The label key
 *   `'walkthrough'` colliding in name with the type value `'walkthrough'` is a
 *   naming coincidence, not a coupling.
 * - `featureFlagSlug` is optional. When absent, the announcement is eligible for all
 *   carriers. When present, it is only eligible while that flag is on for the carrier.
 * - `seenByDefault` is optional. Set it when authoring an announcement for something
 *   carriers already know about, so it's listed in the panel but starts pre-seen instead
 *   of being pushed at login and counted as unread.
 *
 * @typedef {'new-feature' | 'improvement' | 'update' | 'walkthrough'} FeatureAnnouncementLabel
 * @typedef {'walkthrough' | 'info-only'} FeatureAnnouncementType
 *
 * @typedef {Object} FeatureAnnouncementStep
 * @property {string} stepId  A constant from `walkthroughStepIds` (this file) — never a
 *   free-text string authored twice.
 * @property {string} label
 * @property {string} description
 * @property {boolean} [scrollIntoView]  Set when the author already knows this target sits
 *   off-screen on load (e.g. below the fold). Skips the launcher's visibility poll — which
 *   never scrolls, so an off-screen target would just wait out the full timeout — and scrolls
 *   straight to it before checking visibility.
 *
 * @typedef {Object} FeatureAnnouncement
 * @property {string} id  Stable, human-readable, author-assigned slug. Never renamed/reused.
 * @property {string} name
 * @property {string} description
 * @property {string} image  Static asset reference (PNG/JPG/GIF).
 * @property {string} publishDate  ISO date string, identical for every carrier. The ceiling
 *   counts from here, and so does the login-push window for ungated announcements (a gated
 *   one counts from whenever its flag turned on for that carrier).
 * @property {FeatureAnnouncementLabel} label  Display category. Independent of `type`.
 * @property {string} [labelText]  Overrides the default display text for `label`.
 * @property {FeatureAnnouncementType} type  Decides whether the card offers a walkthrough
 *   CTA. It does not change what clicking a panel row does — every row opens the card.
 * @property {string} [ctaText]  Overrides the default "Show Me" link text (walkthrough-type only).
 * @property {string} [featureFlagSlug]  Optional display precondition (see rules above).
 * @property {number} [expirationDays]  Overrides how long this is pushed at login (30 days
 *   from the carrier's first-eligible date).
 * @property {number} [expirationCeilingDays]  Overrides how long this is available at all —
 *   listed and selectable in the What's New panel (365 days from `publishDate`).
 * @property {boolean} [seenByDefault]  Marks the announcement pre-seen for every carrier, so
 *   it never gets pushed at login and never counts toward the unread badge. Still listed and
 *   readable in the What's New panel, just without the unread state. Use for an announcement
 *   backfilled for a feature carriers already know about.
 * @property {string} [route]  Walkthrough-type only: the one page every step's target lives
 *   on. The launcher navigates here before starting. Deliberately per-announcement, not
 *   per-step: a walkthrough only ever shows steps that are visible when it starts, so steps
 *   cannot span pages.
 * @property {string} [setup]  Walkthrough-type only: a key from `walkthroughSetupIds`,
 *   naming a side effect that puts the UI into the state the steps need (e.g. opening a
 *   sidebar) before the first step is shown. Never undone afterwards.
 * @property {FeatureAnnouncementStep[]} [steps]  Present only when `type: 'walkthrough'`.
 */

/**
 * Shared step-ID constants for the Guided Walkthrough feature.
 *
 * A `steps[].stepId` entry below and the component that calls
 * `useWalkthroughTarget(stepId)` for that step must both reference the same
 * constant from here rather than typing matching strings independently —
 * a typo becomes an import error instead of a silently skipped step.
 *
 * Add one entry per walkthrough step as new Walkthrough-type announcements
 * are authored. Usage:
 *
 * // components/Search/Section.vue
 * const savedSearchesButtonTarget = useWalkthroughTarget(walkthroughStepIds.savedSearchesButton);
 */
export const walkthroughStepIds = {
  savedSearchesButton: 'saved-searches-button',
  savedSearchCreateButton: 'saved-search-create-button',
  carrierTMSButton: 'carrier-tms-button',
};

/**
 * Values a walkthrough can put in its `setup` field to have some UI opened before it starts.
 *
 * A walkthrough only shows steps whose targets are on screen, so a step pointing at something
 * inside a closed panel needs that panel opened first — that's what a `setup` is for.
 *
 * Only the names live here; the code each one runs is in `composables/walkthroughSetups.js`,
 * which is free to import stores. This file stays plain data.
 */
export const walkthroughSetupIds = {
  /** Opens the Saved Searches sidebar, so steps on the controls inside it can be shown. */
  openSavedSearchSidebar: 'open-saved-search-sidebar',
};

/**
 * How long an announcement is *pushed at login*, in days, measured from each carrier's
 * first-eligible date. The shorter of the two windows: after this the modal stops opening
 * itself, though the announcement is still there to be read from the What's New panel.
 */
export const DEFAULT_EXPIRATION_DAYS = 30;

/**
 * How long an announcement is *available at all*, in days, measured from `publishDate` —
 * global rather than per-carrier, because how old a feature is doesn't vary by carrier.
 *
 * This is the announcement's whole lifetime: while inside it, the announcement is listed in
 * the What's New panel and selectable there; once past it, the announcement is gone from the
 * panel, stops counting toward the unread badge, and can no longer be pushed at login even
 * for a carrier whose flag only just turned on.
 */
export const DEFAULT_EXPIRATION_CEILING_DAYS = 365;

/** Ids marked seen once onboarded — their topics are already taught by the checklist. */
export const onboardingCoveredFeatureAnnouncementIds = [
  'transportation-management-system',
  'saved-searches',
  'instant-book',
];

/**
 * Published Feature Announcements, oldest-first by convention (release order).
 * Empty until the first real announcement is authored.
 *
 * @type {FeatureAnnouncement[]}
 */
export const featureAnnouncements = [
  {
    id: 'transportation-management-system',
    name: 'Transportation Management System',
    description:
      'Select your preferred system for managing your loads.  We will use it to automatically sync loads and updates.  Change this from your Profile setting.',
    image: '',
    publishDate: '2026-08-01',
    type: 'walkthrough',
    ctaText: 'Show Me',
    route: '/carrier-profile/your-profile',
    steps: [
      {
        stepId: walkthroughStepIds.carrierTMSButton,
        label: 'Transportation Management System',
        description: 'Click here to select your preferred transportation management system.',
        scrollIntoView: true,
      },
    ],
  },
  {
    id: 'saved-searches',
    name: 'Saved Searches',
    description:
      'Save your preferred filter combinations and access them anytime with a single tap, so you can find the right jobs faster without re-entering your criteria.',
    image: '',
    publishDate: '2026-06-20',
    type: 'walkthrough',
    ctaText: 'Show Me',
    route: '/',
    setup: walkthroughSetupIds.openSavedSearchSidebar,
    seenByDefault: true,
    steps: [
      {
        stepId: walkthroughStepIds.savedSearchesButton,
        label: 'Saved Searches',
        description:
          'Start here. This opens the list of searches you have saved, so you can jump straight back to a filter combination you use often.',
      },
      {
        stepId: walkthroughStepIds.savedSearchCreateButton,
        label: 'Create a Saved Search',
        description:
          'Name the filters you are using right now and they will be waiting here next time.',
      },
    ],
  },
  {
    id: 'instant-book',
    name: 'Introducing Instant Book!',
    description: 'Book this job instantly from the job list to claim it at the posted price',
    image: '/images/instant-book-feature-announcement-image.png',
    publishDate: '2026-07-22',
    type: 'info-only',
    featureFlagSlug: featureFlagSlugs.instantBook,
    seenByDefault: true,
  },
];
